'use client';

/**
 * Client login — BFF-only (no browser → IdP).
 * Visual: DESIGN.md “Espresso & Champagne Desk” via login.css --login-* tokens.
 * CSRF: submit stays disabled until token is ready; every submit re-syncs
 * the header to the cookie so a cancelled mint cannot force a page reload.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Button, Input, Form } from 'antd';
import {
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  LockOutlined,
  LineChartOutlined,
  MailOutlined,
  FundOutlined,
  SwapOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useTheme } from '@/shared/theme';
import { AuthNavbar } from '@/domains/auth/components/AuthNavbar';
import {
  awaitPostLogoutApi,
  clearSigningOutFlag,
} from '@/domains/auth/utils/sign-out';
import {
  CSRF_UNAVAILABLE,
  fetchCsrfToken,
  fetchCsrfTokenWithRetry,
  isLoginReauthError,
  loginQueryErrorMessage,
  resetCsrfTokenInflight,
} from '@/domains/auth/utils/login-recovery';
import '@/shared/theme/styles/login.css';
import styles from './ClientLoginPage.module.css';

interface ClientLoginPageProps {
  audience?: 'client';
}

/** Compact clock for wait states — sole time authority (not prose). */
function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const RATE_LIMIT_COPY =
  'Too many sign-in attempts. Sign-in is briefly paused — try again when the timer ends.';

/**
 * Capability stack for technical operators (PRODUCT.md).
 * Full presence kept — layering/weight carries hierarchy, not content removal.
 */
/** Capability list — plain tool names; entitlement is stated once below the list. */
const VALUE_LINES = [
  { icon: <LineChartOutlined aria-hidden />, text: 'Live market prices and charts' },
  { icon: <FundOutlined aria-hidden />, text: 'Positions, balances, and P&L' },
  { icon: <RobotOutlined aria-hidden />, text: 'Trading agent controls' },
  { icon: <SwapOutlined aria-hidden />, text: 'Order placement and fill monitoring' },
] as const;

/**
 * Single frosted workspace under the form — one product surface, internal panes.
 * Not floating stickers: depth-of-field atmosphere for “what’s inside.”
 */
function FormBayCharts() {
  const candles = [
    [16, 52, 18],
    [32, 44, 14],
    [48, 48, 16],
    [64, 36, 20],
    [80, 40, 12],
    [96, 30, 16],
    [112, 34, 14],
    [128, 26, 12],
    [144, 30, 10],
    [160, 24, 14],
    [176, 28, 12],
    [192, 22, 10],
  ] as const;

  return (
    <div className={styles.chartBackdrop} aria-hidden>
      <div className={styles.glassShell}>
        <div className={styles.glassChrome}>
          <span className={styles.glassChromeLeft}>
            <span className={styles.glassMark} aria-hidden />
            <span className={styles.glassTitle}>trading · workspace</span>
          </span>
          <span className={styles.glassChromeRight}>
            <span className={styles.glassLive}>live</span>
            <span className={styles.glassClock}>09:41:02</span>
          </span>
        </div>

        <div className={styles.glassLayout}>
          {/* Primary — equity fills the eye line behind the form */}
          <section className={styles.glassMain}>
            <div className={styles.glassPaneHead}>
              <span>portfolio value</span>
              <span className={styles.glassPaneMeta}>+2.4%</span>
            </div>
            <svg className={styles.glassSvg} viewBox="0 0 480 160" preserveAspectRatio="none">
              {[40, 70, 100, 130].map((y) => (
                <line key={y} className={styles.chartGrid} x1="0" y1={y} x2="480" y2={y} />
              ))}
              <path
                className={styles.chartArea}
                d="M0 128 C48 120 72 108 110 112 C150 116 170 88 210 82 C250 76 270 98 320 64 C370 30 400 44 480 36 L480 160 L0 160 Z"
              />
              <path
                className={styles.chartLine}
                d="M0 128 C48 120 72 108 110 112 C150 116 170 88 210 82 C250 76 270 98 320 64 C370 30 400 44 480 36"
                strokeLinecap="round"
              />
              <path
                className={styles.chartLineMuted}
                d="M0 118 C60 110 120 116 180 100 C240 84 320 92 480 70"
                strokeDasharray="4 6"
                strokeLinecap="round"
              />
            </svg>
          </section>

          {/* Secondary strip — three modules, one toolbar row */}
          <div className={styles.glassStrip}>
            <section className={styles.glassCell}>
              <div className={styles.glassPaneHead}>
                <span>market</span>
              </div>
              <svg className={styles.glassSvg} viewBox="0 0 220 72" preserveAspectRatio="none">
                {candles.map(([x, base, body], i) => {
                  const up = i % 3 !== 1;
                  return (
                    <g key={x} className={up ? styles.candleUp : styles.candleDown}>
                      <line
                        x1={x}
                        y1={base - body * 0.5}
                        x2={x}
                        y2={base + body * 0.35}
                        strokeWidth="1.1"
                      />
                      <rect
                        x={x - 3.5}
                        y={base - (up ? body * 0.3 : 0)}
                        width="7"
                        height={Math.max(6, body * 0.45)}
                        rx="0.5"
                      />
                    </g>
                  );
                })}
              </svg>
            </section>

            <section className={styles.glassCell}>
              <div className={styles.glassPaneHead}>
                <span>exposure</span>
              </div>
              <svg className={styles.glassSvg} viewBox="0 0 180 72" preserveAspectRatio="none">
                {[
                  [14, 42],
                  [36, 28],
                  [58, 36],
                  [80, 20],
                  [102, 32],
                  [124, 16],
                  [146, 26],
                ].map((pair, i) => {
                  const x = pair[0] ?? 0
                  const h = pair[1] ?? 0
                  return (
                    <rect
                      key={x}
                      className={i % 2 === 0 ? styles.barPrimary : styles.barMuted}
                      x={x}
                      y={64 - h}
                      width="12"
                      height={h}
                      rx="1"
                    />
                  )
                })}
              </svg>
            </section>

            <section className={styles.glassCell}>
              <div className={styles.glassPaneHead}>
                <span>agents</span>
                <span className={styles.glassPaneMeta}>4</span>
              </div>
              <svg className={styles.glassSvg} viewBox="0 0 160 72" preserveAspectRatio="none">
                <path
                  className={styles.chartLine}
                  d="M6 50 L24 48 L42 52 L60 36 L78 40 L96 28 L114 32 L132 22 L154 26"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle className={styles.chartDot} cx="154" cy="26" r="2.2" />
              </svg>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandPanel() {
  return (
    <aside className={styles.brandPanel} aria-label="Mechagora trading workspace">
      <div className={styles.brandField} aria-hidden>
        <span className={`${styles.brandRing} ${styles.brandRingOuter}`} />
        <span className={`${styles.brandRing} ${styles.brandRingMid}`} />
        <span className={`${styles.brandRing} ${styles.brandRingInner}`} />
        <div className={styles.brandMedallion}>
          <span>M</span>
        </div>
      </div>

      <div className={styles.brandContent}>
        <div className={styles.brandTop}>
          <p className={styles.brandMeta}>
            <span className={styles.brandMetaTag}>Client</span>
            <span className={styles.brandMetaSep} aria-hidden>
              /
            </span>
            <span>Trading workspace</span>
          </p>
          <h2 className={styles.brandHeading}>
            Markets, portfolio, agents,
            <br />
            <span className={styles.brandHeadingAccent}>and orders in one workspace.</span>
          </h2>
          <p className={styles.brandDescription}>
            Sign in to review live markets and your portfolio. Paid plans also include
            trading agent controls and order execution.
          </p>
        </div>

        <div className={styles.valueBlock}>
          <p className={styles.valueBlockLabel}>Available tools</p>
          <ul className={styles.valueList}>
            {VALUE_LINES.map((line) => (
              <li key={line.text} className={styles.valueLine}>
                <span className={styles.valueIcon}>{line.icon}</span>
                <span className={styles.valueText}>{line.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className={styles.brandFoot}>
          Your plan controls which trading tools are available.
        </p>
      </div>
    </aside>
  );
}

const ClientLoginPage: React.FC<ClientLoginPageProps> = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [csrfReady, setCsrfReady] = useState(false);
  const [csrfFailed, setCsrfFailed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [retryAfterSec, setRetryAfterSec] = useState<number | null>(null);
  const [retryTotalSec, setRetryTotalSec] = useState<number | null>(null);
  const theme = useTheme();
  const [form] = Form.useForm();
  const dark = theme.isDark;
  const errorRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const csrfReadyRef = useRef(false);
  const rateLimitTicking = retryAfterSec != null && retryAfterSec > 0;

  const applyCsrfResult = useCallback((token: string | null): string | null => {
    if (token) {
      csrfReadyRef.current = true;
      setCsrfReady(true);
      setCsrfFailed(false);
      return token;
    }
    csrfReadyRef.current = false;
    setCsrfFailed(true);
    setCsrfReady(false);
    return null;
  }, []);

  const fetchCsrf = useCallback(async (rotate = false): Promise<string | null> => {
    try {
      return applyCsrfResult(await fetchCsrfToken(undefined, { rotate }));
    } catch {
      return applyCsrfResult(null);
    }
  }, [applyCsrfResult]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!rateLimitTicking) return;
    const id = window.setInterval(() => {
      setRetryAfterSec((prev) => {
        if (prev == null) return null;
        if (prev <= 1) {
          setError(null);
          setRetryTotalSec(null);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [rateLimitTicking]);

  const errorCode = searchParams.get('error');
  const returnToParam = searchParams.get('return_to') ?? searchParams.get('returnTo');

  useEffect(() => {
    const message = loginQueryErrorMessage(errorCode);
    if (message) setError(message);
  }, [errorCode]);

  // Live session only — expired cookies must not bounce this page back to the desk.
  useEffect(() => {
    if (isLoginReauthError(errorCode)) return;
    let cancelled = false;
    void fetch('/api/auth/verify-client', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok || cancelled) return;
        const data = (await response.json()) as { success?: boolean; user?: unknown };
        if (!data.success || !data.user || cancelled) return;
        const raw = returnToParam ?? '/client/dashboard';
        const path = raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/auth')
          ? raw
          : '/client/dashboard';
        window.location.replace(path);
      })
      .catch(() => {
        /* stay on login */
      });
    return () => {
      cancelled = true;
    };
  }, [errorCode, returnToParam]);

  // Focus recovery: alert first, then email for re-entry
  useEffect(() => {
    if (!error || rateLimitTicking) return;
    const t = window.setTimeout(() => {
      errorRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [error, rateLimitTicking]);

  // Bootstrap CSRF before submit can succeed (prevents 403 race).
  // Re-auth: clear leftover session cookies first, then retry CSRF in-place
  // so the client never has to reload the page to sign back in.
  useEffect(() => {
    let alive = true;
    clearSigningOutFlag();
    // Soft-nav / Strict Mode can leave a hung single-flight promise; always
    // start minting from a clean slate on this mount.
    resetCsrfTokenInflight();

    const failSafe = window.setTimeout(() => {
      if (!alive || csrfReadyRef.current) return;
      // Never leave the bezel on "preparing" forever — surface retry.
      applyCsrfResult(null);
    }, 12_000);

    const prepare = async (clearSession: boolean) => {
      // Logout no longer clears csrf_token. Do not block minting on it —
      // a hung keepalive logout was leaving the form on "Preparing sign-in".
      if (clearSession) {
        void awaitPostLogoutApi(2000);
      }
      const token = await fetchCsrfTokenWithRetry({
        attempts: 4,
        delaysMs: [0, 200, 500, 1000],
        deadlineMs: 10_000,
      });
      if (!alive) return;
      applyCsrfResult(token);
    };

    const reauth = isLoginReauthError(errorCode);
    void prepare(reauth);

    const retryIfNeeded = () => {
      if (document.visibilityState === 'hidden') return;
      if (csrfReadyRef.current) return;
      resetCsrfTokenInflight();
      void fetchCsrfToken()
        .then((token) => {
          if (alive && token) applyCsrfResult(token);
        })
        .catch(() => {
          /* keep current state; Retry control remains */
        });
    };
    window.addEventListener('visibilitychange', retryIfNeeded);
    window.addEventListener('focus', retryIfNeeded);

    return () => {
      alive = false;
      window.clearTimeout(failSafe);
      window.removeEventListener('visibilitychange', retryIfNeeded);
      window.removeEventListener('focus', retryIfNeeded);
    };
  }, [applyCsrfResult, errorCode]);

  const startRateLimit = (seconds: number) => {
    const s = Math.max(1, Math.ceil(seconds));
    setRetryAfterSec(s);
    setRetryTotalSec(s);
    setError(null);
  };

  const focusFirstInvalid = () => {
    const emailErrs = form.getFieldError('email');
    const passErrs = form.getFieldError('password');
    if (emailErrs.length) {
      emailInputRef.current?.focus();
      return;
    }
    if (passErrs.length) {
      passwordInputRef.current?.focus();
    }
  };

  const postLogin = async (
    token: string,
    values: { email: string; password: string },
    returnTo: string | undefined
  ) => {
    const res = await fetch('/api/auth/zitadel/client/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': token,
      },
      body: JSON.stringify({
        email: values.email.trim(),
        password: values.password,
        returnTo,
      }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      error?: string;
      redirectTo?: string;
      retryAfter?: number;
    };
    return { res, data };
  };

  const handleSignIn = async (values: { email: string; password: string }) => {
    if (retryAfterSec != null && retryAfterSec > 0) return;

    setLoading(true);
    setError(null);
    setRetryAfterSec(null);
    setRetryTotalSec(null);

    try {
      // Always re-read CSRF immediately before POST. React state can hold a
      // token whose cookie was overwritten by a cancelled/duplicate GET.
      const token = await fetchCsrf(false);
      if (!token) {
        setError(CSRF_UNAVAILABLE);
        return;
      }

      const returnTo = searchParams.get('return_to') ?? undefined;
      let { res, data } = await postLogin(token, values, returnTo);

      // One automatic recovery if CSRF cookie/header raced or expired
      if (res.status === 403) {
        const refreshed = await fetchCsrf(true);
        if (refreshed) {
          ({ res, data } = await postLogin(refreshed, values, returnTo));
        }
        if (res.status === 403) {
          void fetchCsrf(true);
          setError(
            data.error?.toLowerCase().includes('csrf')
              ? 'Sign-in expired. Please try again.'
              : data.error ?? 'Sign-in expired. Please try again.'
          );
          window.setTimeout(() => emailInputRef.current?.focus(), 0);
          return;
        }
      }

      if (!res.ok || !data.success) {
        if (res.status === 429 && typeof data.retryAfter === 'number' && data.retryAfter > 0) {
          startRateLimit(data.retryAfter);
          return;
        }

        setError(
          data.error ?? 'Authentication failed. Check your email and password, then try again.'
        );
        window.setTimeout(() => emailInputRef.current?.focus(), 0);
        return;
      }

      const dest = data.redirectTo ?? '';
      const path = dest.startsWith('/') && !dest.startsWith('//') ? dest : '/client/dashboard';
      // Mark audience + one-shot post-login flag (skip full-viewport re-verify thrash)
      try {
        sessionStorage.setItem('userType', 'client');
        sessionStorage.setItem('mechagora:just_logged_in', String(Date.now()));
        sessionStorage.removeItem('mechagora:signing_out');
      } catch {
        /* no-op */
      }
      // Full navigation so cookie is visible on next document (no soft-router race)
      window.location.assign(path);
    } catch {
      setError('A network error occurred. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const rateLimitActive = retryAfterSec != null && retryAfterSec > 0;
  const submitBlocked = rateLimitActive || !csrfReady || loading;
  const progressPct =
    rateLimitActive && retryTotalSec != null && retryTotalSec > 0
      ? Math.min(100, Math.max(0, Math.round((retryAfterSec! / retryTotalSec) * 100)))
      : 0;

  const submitLabel = rateLimitActive
    ? 'Try again shortly'
    : loading
      ? 'Signing in…'
      : !csrfReady
        ? csrfFailed
          ? 'Sign-in unavailable'
          : 'Preparing sign-in…'
        : 'Sign in';
  const csrfPreparing =
    !csrfReady && !csrfFailed && !rateLimitActive && !loading;

  return (
    <div
      className={`login-page ${dark ? 'dark' : 'light'}`}
      data-theme={dark ? 'dark' : 'light'}
    >
      <AuthNavbar />

      <div className={styles.body}>
        {/* L0 — deep branded field behind the shell */}
        <div className={styles.canvasBrand} aria-hidden>
          <div className={styles.canvasVignette} />
          <div className={styles.canvasOrbA} />
          <div className={styles.canvasOrbB} />
          <div className={styles.canvasMark}>M</div>
          <div className={styles.canvasWord}>MECHAGORA</div>
        </div>

        <div
          className={styles.workspaceShell}
        >
          {!isMobile && <BrandPanel />}

          <main className={styles.formSide} id="main-content" tabIndex={-1}>
            <FormBayCharts />
            {/* Sharp instrument card — same craft language as the workspace chrome */}
            <div className={styles.formCard}>
              <div className={styles.authBezel}>
                <span className={styles.authBezelLeft}>
                  <span className={styles.bezelMark} aria-hidden />
                  <span className={styles.authBezelLabel}>secure sign-in</span>
                </span>
                <span
                  className={`${styles.authBezelStatus} ${csrfReady
                    ? styles.authBezelReady
                    : csrfFailed
                      ? styles.authBezelError
                      : ''
                    }`}
                >
                  {csrfReady ? 'ready' : csrfFailed ? 'retry' : 'preparing'}
                </span>
              </div>

              <div className={styles.formContainer}>
                {/* Single h1 — mobileLogo only restyles on small screens (no duplicate heading) */}
                <header className={`${styles.formHeader} ${styles.mobileLogo}`}>
                  <p className={styles.formKicker}>Client account</p>
                  <h1 className={styles.formTitle}>Sign in</h1>
                  <p className={styles.formSubtitle}>
                    Continue to your trading workspace.
                  </p>
                </header>

                {rateLimitActive && (
                  <div
                    className={styles.waitBanner}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <div className={styles.waitHeader}>
                      <ClockCircleOutlined className={styles.waitIcon} aria-hidden />
                      <div className={styles.waitCopy}>
                        <div className={styles.waitTitle}>Sign-in paused</div>
                        <div className={styles.waitMessage}>{RATE_LIMIT_COPY}</div>
                      </div>
                    </div>
                    <div className={styles.waitTimerRow}>
                      <span
                        className={styles.waitClock}
                        aria-label={`${retryAfterSec} seconds remaining`}
                      >
                        {formatClock(retryAfterSec!)}
                      </span>
                      <span className={styles.waitHint}>remaining</span>
                    </div>
                    <div className={styles.waitTrack} aria-hidden>
                      <div
                        className={styles.waitFill}
                        style={{ transform: `scaleX(${Math.max(0, Math.min(1, progressPct / 100))})` }}
                      />
                    </div>
                  </div>
                )}

                {!rateLimitActive && error && (
                  <div
                    ref={errorRef}
                    className={styles.errorAlert}
                    role="alert"
                    tabIndex={-1}
                    id="login-form-error"
                  >
                    <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
                    <div className={styles.errorContent}>
                      <div className={styles.errorTitle}>Could not sign in</div>
                      <div className={styles.errorText}>{error}</div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => setError(null)}
                      className={styles.dismissBtn}
                      aria-label="Dismiss error"
                    />
                  </div>
                )}

                {csrfFailed && !csrfReady && !error && !rateLimitActive && (
                  <div className={styles.csrfHint} role="status" aria-live="polite">
                    <ExclamationCircleOutlined aria-hidden />
                    <span>
                      Sign-in could not be prepared. Try again — no page reload needed.{' '}
                      <button
                        type="button"
                        className={styles.csrfRetry}
                        onClick={() => void fetchCsrfTokenWithRetry().then(applyCsrfResult)}
                      >
                        Retry
                      </button>
                    </span>
                  </div>
                )}

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSignIn}
                  onFinishFailed={focusFirstInvalid}
                  scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                  requiredMark={false}
                  className={styles.formFields}
                  disabled={rateLimitActive || loading}
                  aria-busy={loading || !csrfReady}
                >
                  <Form.Item
                    name="email"
                    htmlFor="client-login-email"
                    label={<span className={styles.fieldLabel}>Email</span>}
                    rules={[
                      { required: true, message: 'Enter your email address' },
                      { type: 'email', message: 'Enter a valid email address' },
                    ]}
                    validateTrigger={['onBlur', 'onSubmit']}
                    hasFeedback={false}
                  >
                    <Input
                      ref={(node) => {
                        emailInputRef.current = node?.input ?? null;
                      }}
                      prefix={<MailOutlined aria-hidden />}
                      type="email"
                      name="email"
                      id="client-login-email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="email"
                      aria-required="true"
                      aria-describedby={error && !rateLimitActive ? 'login-form-error' : undefined}
                    />
                  </Form.Item>

                  <div className={styles.fieldRow}>
                    <label
                      className={styles.fieldLabel}
                      htmlFor="client-login-password"
                      id="password-label"
                    >
                      Password
                    </label>
                    <a href="/auth/forgot-password" className={styles.forgotLink}>
                      Forgot password?
                    </a>
                  </div>
                  <Form.Item
                    name="password"
                    htmlFor="client-login-password"
                    rules={[{ required: true, message: 'Enter your password' }]}
                    validateTrigger={['onBlur', 'onSubmit']}
                    className={styles.passwordItem}
                    hasFeedback={false}
                  >
                    <Input.Password
                      ref={(node) => {
                        const anyNode = node as unknown as {
                          input?: HTMLInputElement;
                          nativeElement?: HTMLInputElement;
                        } | null;
                        passwordInputRef.current =
                          anyNode?.input ?? anyNode?.nativeElement ?? null;
                      }}
                      prefix={<LockOutlined aria-hidden />}
                      id="client-login-password"
                      name="password"
                      placeholder="Your password"
                      autoComplete="current-password"
                      aria-labelledby="password-label"
                      aria-required="true"
                      aria-describedby={error && !rateLimitActive ? 'login-form-error' : undefined}
                    />
                  </Form.Item>

                  <div className={styles.formActions}>
                    <Button
                      type="primary"
                      block
                      htmlType="submit"
                      icon={
                        !submitBlocked && csrfReady ? <ArrowRightOutlined /> : undefined
                      }
                      iconPlacement="end"
                      loading={loading}
                      disabled={submitBlocked || !csrfReady}
                      aria-disabled={submitBlocked || !csrfReady}
                      data-state={csrfPreparing ? 'preparing' : undefined}
                      className={`${styles.submitBtn} ${submitBlocked && !csrfPreparing ? styles.submitBtnDisabled : ''
                        }`}
                    >
                      {submitLabel}
                    </Button>

                    {!csrfReady && !csrfFailed && !rateLimitActive && (
                      <span className={styles.srOnly} aria-live="polite">
                        Preparing sign-in…
                      </span>
                    )}

                    <div className={styles.divider} role="separator">
                      <div className={styles.dividerLine} />
                      <span className={styles.dividerText}>New here?</span>
                      <div className={styles.dividerLine} />
                    </div>

                    <Button
                      type="default"
                      block
                      className={styles.secondaryBtn}
                      onClick={() => router.push('/auth/client-register')}
                    >
                      Create free account
                    </Button>
                  </div>
                </Form>

                <footer className={styles.formFooter}>
                  <p className={styles.formFootnote}>
                    By signing in you agree to our{' '}
                    <a href="/legal/terms" className={styles.legalLink}>
                      Terms
                    </a>{' '}
                    and{' '}
                    <a href="/legal/privacy" className={styles.legalLink}>
                      Privacy Policy
                    </a>
                    . See also{' '}
                    <a href="/legal/disclosures" className={styles.legalLink}>
                      Disclosures
                    </a>
                    .
                  </p>
                </footer>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ClientLoginPage;
